import {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { Select, Drawer, Button, Typography, message, Modal, Input } from "antd";

import "survey-core/defaultV2.min.css";

import { StylesManager, Model, surveyLocalization } from "survey-core";
import { Survey } from "survey-react-ui";
import {
  loadSurveyDraftSession,
  registerSurveyDraftFlusher,
  saveSurveyDraftSession,
  unregisterSurveyDraftFlusher,
  isSurveyFailedSubmissionRecord,
  type SurveyFailedSubmissionRecord,
} from "@/utils/surveyOfflinePersistence";

StylesManager.applyTheme("defaultV2");

/** Staff-only backup upload gate (SurveyWebSPA sidebar). */
const STAFF_BACKUP_ACCESS_CODE = "ICCMW";

interface ISurveyContent {
  json: string;
  data?: any;
  showCompletedPage?: boolean;
  mode?: "edit" | "display";
  onComplete?(value: any): any;
  /** Session-only partial draft key */
  draftStorageKey?: string;
  /** When set, staff-only control in sidebar reveals “Upload JSON” after click. */
  backupFileUpload?: {
    expectedContext: string;
    onRestoreFromFile: (
      record: SurveyFailedSubmissionRecord
    ) => void | Promise<void>;
  };
}

interface LocaleItem {
  localeCode: string;
  nativeName: string;
  englishName: string;
  languageName: string;
}

const LOCALE_LIST: LocaleItem[] = [
  {
    localeCode: "en",
    nativeName: "English",
    englishName: "English",
    languageName: "Language",
  },
  {
    localeCode: "sc",
    nativeName: "简体中文",
    englishName: "Simplified Chinese",
    languageName: "语言",
  },
  {
    localeCode: "tc",
    nativeName: "繁體中文",
    englishName: "Traditional Chinese",
    languageName: "語言",
  },
];

const getLocaleItem = (code: string) =>
  LOCALE_LIST.find((v) => v.localeCode === code);

function hasMeaningfulSurveyData(data: any): boolean {
  if (data === undefined || data === null || data === "") return false;
  if (typeof data === "object" && data !== null && !Array.isArray(data)) {
    return Object.keys(data).length > 0;
  }
  return true;
}

/**
 * Form designer (survey-core 2.x) saves `showProgressBar` as a boolean.
 * This app's survey-core 1.9.80 only accepts "off" | "top" | "bottom" | "both"
 * and calls `toLowerCase()` in the setter, which throws on a boolean and
 * leaves the survey page blank.
 */
function normalizeShowProgressBar(json: string): string {
  if (!json) return json;
  let schema: Record<string, unknown>;
  try {
    schema = JSON.parse(json);
  } catch {
    return json;
  }
  if (!schema || typeof schema !== "object" || Array.isArray(schema)) return json;
  if (typeof schema.showProgressBar !== "boolean") return json;

  const location =
    typeof schema.progressBarLocation === "string"
      ? schema.progressBarLocation.toLowerCase()
      : "";

  if (schema.showProgressBar === false) {
    schema.showProgressBar = "off";
  } else if (location === "bottom") {
    schema.showProgressBar = "bottom";
  } else if (location === "topbottom" || location === "both") {
    schema.showProgressBar = "both";
  } else {
    schema.showProgressBar = "top";
  }

  return JSON.stringify(schema);
}

function useStableSurveyModel(json: string): Model {
  const cacheRef = useRef<{ schema: string; model: Model } | null>(null);

  if (!cacheRef.current || cacheRef.current.schema !== json) {
    const m = new Model(normalizeShowProgressBar(json));
    cacheRef.current = { schema: json, model: m };
  }

  return cacheRef.current.model;
}

const SurveyContent = ({
  json,
  data,
  mode = "edit",
  showCompletedPage = false,
  onComplete,
  draftStorageKey,
  backupFileUpload,
}: ISurveyContent) => {
  surveyLocalization.supportedLocales = LOCALE_LIST.filter(
    (v) => v.localeCode !== "en"
  ).map((v) => v.localeCode);

  const survey = useStableSurveyModel(json);

  const initialLocale = survey?.locale ?? "en";
  const initialLocaleItem = getLocaleItem(initialLocale);

  const [selectedLocale, setSelectedLocale] = useState<string>(initialLocale);
  const [selectedLanguageName, setSelectedLanguageName] = useState<string>(
    initialLocaleItem?.languageName ?? "Language"
  );
  const [newMode, setNewMode] = useState(mode);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [staffPasswordModalOpen, setStaffPasswordModalOpen] = useState(false);
  const [staffPasswordDraft, setStaffPasswordDraft] = useState("");
  const [staffBackupAccessGranted, setStaffBackupAccessGranted] =
    useState(false);
  const [staffBackupUploadPanelVisible, setStaffBackupUploadPanelVisible] =
    useState(false);

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const didProgrammaticComplete = useRef(false);

  const dataSignature =
    data === undefined || data === null
      ? ""
      : typeof data === "string"
        ? data
        : JSON.stringify(data);

  const persistDraftFromModel = useCallback(() => {
    if (!draftStorageKey || newMode !== "edit" || showCompletedPage) return;
    try {
      const raw = survey.data;
      const snapshot =
        raw != null && typeof raw === "object"
          ? (JSON.parse(JSON.stringify(raw)) as Record<string, unknown>)
          : {};
      saveSurveyDraftSession(draftStorageKey, snapshot);
    } catch (e) {
      console.warn("saveSurveyDraftSession", e);
    }
  }, [survey, draftStorageKey, newMode, showCompletedPage]);

  useEffect(() => {
    if (!draftStorageKey || newMode !== "edit" || showCompletedPage) return;
    registerSurveyDraftFlusher(draftStorageKey, persistDraftFromModel);
    return () => unregisterSurveyDraftFlusher(draftStorageKey);
  }, [draftStorageKey, newMode, showCompletedPage, persistDraftFromModel]);

  useEffect(() => {
    setNewMode(mode);
  }, [mode]);

  useEffect(() => {
    if (!backupFileUpload) {
      setStaffPasswordModalOpen(false);
      setStaffPasswordDraft("");
      setStaffBackupAccessGranted(false);
      setStaffBackupUploadPanelVisible(false);
    }
  }, [backupFileUpload]);

  useEffect(() => {
    const loc = survey.locale ?? "en";
    setSelectedLocale(loc);
    const localeItem = getLocaleItem(loc);
    setSelectedLanguageName(localeItem?.languageName ?? "Language");
  }, [survey]);

  useEffect(() => {
    survey.locale = selectedLocale;
  }, [selectedLocale, survey]);

  useEffect(() => {
    if (!showCompletedPage) {
      didProgrammaticComplete.current = false;
      return;
    }
    if (didProgrammaticComplete.current) return;
    didProgrammaticComplete.current = true;
    survey.doComplete();
  }, [showCompletedPage, survey]);

  useEffect(() => {
    if (showCompletedPage) return;

    if (newMode === "display") {
      if (hasMeaningfulSurveyData(data)) {
        try {
          survey.data =
            typeof data === "string" ? JSON.parse(data) : data;
        } catch {
          survey.data = typeof data === "string" ? data : {};
        }
      }
      return;
    }

    if (hasMeaningfulSurveyData(data)) {
      try {
        survey.data =
          typeof data === "string" ? JSON.parse(data) : data;
      } catch {
        survey.data = typeof data === "string" ? data : {};
      }
      return;
    }

    if (draftStorageKey) {
      const draft = loadSurveyDraftSession(draftStorageKey);
      if (draft && Object.keys(draft).length > 0) {
        survey.data = draft;
        return;
      }
    }
    survey.data = {};
  }, [survey, dataSignature, draftStorageKey, newMode, showCompletedPage]);

  useEffect(() => {
    const defaultOnComplete = () => {};
    const userHandler = (sender: any) => {
      if (draftStorageKey && sender?.data) {
        try {
          const snapshot = JSON.parse(
            JSON.stringify(sender.data)
          ) as Record<string, unknown>;
          saveSurveyDraftSession(draftStorageKey, snapshot);
        } catch (e) {
          console.warn("saveSurveyDraftSession", e);
        }
      }
      onCompleteRef.current?.(sender);
    };
    survey.onComplete.add(defaultOnComplete);
    survey.onComplete.add(userHandler);
    return () => {
      survey.onComplete.remove(defaultOnComplete);
      survey.onComplete.remove(userHandler);
    };
  }, [survey, draftStorageKey]);

  useEffect(() => {
    if (!draftStorageKey || newMode !== "edit" || showCompletedPage) return;
    survey.onValueChanged.add(persistDraftFromModel);
    survey.onMatrixCellValueChanged.add(persistDraftFromModel);
    survey.onDynamicPanelItemValueChanged.add(persistDraftFromModel);
    return () => {
      survey.onValueChanged.remove(persistDraftFromModel);
      survey.onMatrixCellValueChanged.remove(persistDraftFromModel);
      survey.onDynamicPanelItemValueChanged.remove(persistDraftFromModel);
    };
  }, [survey, draftStorageKey, newMode, showCompletedPage, persistDraftFromModel]);

  const handleLocaleChange = useCallback((locale: string) => {
    setSelectedLocale(locale);
    const localeItem = getLocaleItem(locale);
    setSelectedLanguageName(localeItem?.languageName ?? "Language");
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const usedLocales = LOCALE_LIST.filter((loc) =>
    survey.getUsedLocales().includes(loc.localeCode)
  );

  const backupFileInputRef = useRef<HTMLInputElement>(null);

  const handleBackupFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!backupFileUpload) return;
    const file = e.target.files?.[0];
    if (!file) {
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const parsed: unknown = JSON.parse(reader.result as string);
        if (!isSurveyFailedSubmissionRecord(parsed)) {
          message.error("Invalid backup file: expected a survey backup export.");
          return;
        }
        if (parsed.context !== backupFileUpload.expectedContext) {
          message.error(
            `This backup is for "${parsed.context}", not this form (${backupFileUpload.expectedContext}).`
          );
          return;
        }
        await Promise.resolve(backupFileUpload.onRestoreFromFile(parsed));
      } catch {
        message.error("Could not read the JSON file.");
      } finally {
        e.target.value = "";
      }
    };
    reader.onerror = () => {
      message.error("Could not read the file.");
      e.target.value = "";
    };
    reader.readAsText(file);
  };

  const handleStaffBackupButtonClick = () => {
    if (!staffBackupAccessGranted) {
      setStaffPasswordDraft("");
      setStaffPasswordModalOpen(true);
      return;
    }
    setStaffBackupUploadPanelVisible((v) => !v);
  };

  const handleStaffPasswordOk = () =>
    new Promise<void>((resolve, reject) => {
      if (staffPasswordDraft.trim() !== STAFF_BACKUP_ACCESS_CODE) {
        message.error("密碼錯誤");
        reject();
        return;
      }
      setStaffBackupAccessGranted(true);
      setStaffBackupUploadPanelVisible(true);
      setStaffPasswordModalOpen(false);
      setStaffPasswordDraft("");
      resolve();
    });

  const backupUploadSidebarBlock =
    backupFileUpload != null ? (
      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: "1px solid #f0f0f0",
        }}
      >
        <Button
          type="primary"
          size="small"
          block
          icon={
            staffBackupAccessGranted ? <UnlockOutlined /> : <LockOutlined />
          }
          onClick={handleStaffBackupButtonClick}
          style={{
            marginBottom:
              staffBackupAccessGranted && staffBackupUploadPanelVisible
                ? 10
                : 0,
          }}
        >
          愛羣同工專用
        </Button>
        {staffBackupAccessGranted && staffBackupUploadPanelVisible ? (
          <>
            <Typography.Text
              type="secondary"
              style={{ display: "block", marginBottom: 8 }}
            >
              Restore from a downloaded JSON file and click complete.
            </Typography.Text>
            <input
              ref={backupFileInputRef}
              type="file"
              accept=".json,application/json"
              style={{ display: "none" }}
              onChange={handleBackupFileChange}
            />
            <Button
              size="small"
              onClick={() => {
                const input = backupFileInputRef.current;
                if (input) input.value = "";
                input?.click();
              }}
            >
              Upload JSON
            </Button>
          </>
        ) : null}
      </div>
    ) : null;

  const LocaleSelector = useCallback(
    () => (
      <Select
        value={selectedLocale}
        onChange={handleLocaleChange}
        style={{ width: "100%" }}
      >
        {usedLocales.map((loc) => (
          <Select.Option key={loc.localeCode} value={loc.localeCode}>
            {loc.nativeName}
          </Select.Option>
        ))}
      </Select>
    ),
    [selectedLocale, handleLocaleChange, usedLocales]
  );

  return (
    <div className="ant-layout-content" style={{ padding: isMobile ? 12 : undefined }}>
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 16,
          alignItems: isMobile ? "stretch" : "flex-start",
        }}
      >
        <div style={{ flex: 1 }}>
          <Survey model={survey} mode={newMode} />
        </div>

        {!isMobile && (
          <div
            style={{
              width: 240,
              padding: 12,
              borderLeft: "1px solid #f0f0f0",
            }}
          >
            <div style={{ marginBottom: 8, fontWeight: 600 }}>
              {selectedLanguageName}
            </div>
            <LocaleSelector />
            {backupUploadSidebarBlock}
          </div>
        )}
      </div>

      {isMobile && (
        <>
          <Button
            type="primary"
            shape="circle"
            onClick={() => setDrawerVisible(true)}
            style={{ position: "fixed", right: 12, bottom: 12, zIndex: 1000 }}
            title={selectedLanguageName}
          >
            {selectedLocale.toUpperCase()}
          </Button>

          <Drawer
            title={selectedLanguageName}
            placement="right"
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
            bodyStyle={{ paddingTop: 8 }}
          >
            <div style={{ marginBottom: 8, fontWeight: 600 }}>
              {selectedLanguageName}
            </div>
            <LocaleSelector />
            {backupUploadSidebarBlock}
          </Drawer>
        </>
      )}

      <Modal
        title="愛羣同工專用"
        open={staffPasswordModalOpen}
        onOk={handleStaffPasswordOk}
        onCancel={() => {
          setStaffPasswordModalOpen(false);
          setStaffPasswordDraft("");
        }}
        okText="OK"
        cancelText="Cancel"
        destroyOnClose
        getContainer={() => document.body}
      >
        <Input.Password
          placeholder="請輸入密碼"
          value={staffPasswordDraft}
          onChange={(e) => setStaffPasswordDraft(e.target.value)}
          autoComplete="off"
        />
      </Modal>
    </div>
  );
};

export default SurveyContent;
